// @flow strict
import * as React from "react"
import * as R from "ramda"
import { connect } from "react-redux"
import type { Dispatch } from "redux"
import type { Connector } from "react-redux"
import type { Currencies } from "@kiwicom/nitro/lib/records/Currency"
import type { Session } from "@kiwicom/nitro/lib/records/Session"
import { Provider as SessionProvider } from "@kiwicom/nitro/lib/services/session/context"
import { Provider as CurrencyProvider } from "@kiwicom/nitro/lib/services/currency/context"
import type { IntlRaw } from "@kiwicom/nitro/lib/records/Intl"
import { Provider as IntlProvider } from "@kiwicom/nitro/lib/services/intl/context"
import { Provider as ModalProvider } from "@kiwicom/nitro/lib/services/modal/context"
import InitIntl from "@kiwicom/nitro/lib/components/InitIntl"
import { Map } from "immutable"
import { Provider as AuthProvider } from "@kiwicom/nitro/lib/services/auth/context"
import makeEnvironment from "@kiwicom/nitro/lib/services/utils/relay"
import type { MyBookingInput, RegisterInput } from "@kiwicom/nitro/lib/services/auth/api"
import type { SocialProvider } from "@kiwicom/nitro/lib/records/Auth"
import { NONE, MY_BOOKING, REGISTER, SIGN_IN } from "@kiwicom/nitro/lib/consts/modals"
import type { Modal } from "@kiwicom/nitro/lib/consts/modals"
import rewriteCurrencies from "@kiwicom/nitro/lib/services/currency/services/rewriteCurrencies"

import mmbRedirect from "../../services/auth/mmbRedirect"
// auth
import * as user from "../../services/user"
import type { UserStateInst } from "../../services/user/types"
import type User from "../../records/User"
// currency
import { getCurrency, getLanguage, getOptions } from "../../services/options/reducer"
import { setCurrency } from "../../services/options/actions"
import { getCurrencies } from "../../services/currencies"
import { showModal } from "../../services/modals/actions"
import { getRecommendedCurrenciesSelectorJS } from "../../services/currencies/selectors"
import authStore from "../../services/user/authStore"
import { modals } from "../../utils/nitroSideEffects"
import type { TabTypes } from "../../consts/account"

type OwnProps = {|
  children: React.Node,
  intl: IntlRaw,
  session: Session,
|}

type Props = {|
  ...OwnProps,
  lang: string,
  // intl
  show: boolean,
  // auth
  userState: UserStateInst,
  onRegister: (input: RegisterInput) => Promise<void>,
  onSocialAuth: (provider: SocialProvider) => Promise<void>,
  onSignIn: (email: string, password: string) => Promise<void>,
  onSignOut: () => void,
  // currency
  currencyId: string,
  available: Currencies,
  recommended: string[],
  onChange: (value: string) => void,
  // modals
  onChangeModal: (modal: string, options?: {| activeTab: TabTypes |}) => void,
|}

const nitrifyUser = (u: User) => ({
  id: u.id,
  email: u.email,
  verified: u.verified,
  firstname: u.firstname,
  lastname: u.lastname,
})

const getAuth = (u: ?User) => {
  if (u && u.token) {
    return { type: "user", user: nitrifyUser(u), token: u.token }
  }

  if (authStore.kwAuthToken && u) {
    return { type: "magic", email: u.email, token: authStore.kwAuthToken }
  }

  return null
}

const onModalChange = dispatch => (modalType: Modal) => {
  // necessary to remove introduced side effects when opening login modals from Navbar
  modals(NONE)

  if ([MY_BOOKING, REGISTER, SIGN_IN].includes(modalType)) {
    dispatch(showModal("login", { activeTab: modalType }))
    return
  }

  // $FlowFixMe TODO: make it compatible?
  dispatch(showModal(modalType))
}

const NitroBridge = ({
  lang,
  children,
  // intl
  intl,
  show,
  // auth
  userState,
  onRegister,
  onSocialAuth,
  onSignIn,
  onSignOut,
  // currency
  currencyId,
  available,
  recommended,
  onChange,
  // session
  session,
  // modals
  onChangeModal,
}: Props) => (
  <SessionProvider value={session}>
    <AuthProvider
      value={{
        auth: getAuth(userState.user),
        loading: userState.loading,
        environment: makeEnvironment({
          Authorization: userState.user && userState.user.token ? userState.user.token : "",
          "KW-Auth-Token": authStore.kwAuthToken || "",
          "Accept-Language": intl.language.iso,
        }),
        onMyBooking: ({ bid, email, iata, departure }: MyBookingInput) =>
          mmbRedirect(lang, { bid, email, iata, departure }),
        onRegister,
        onSocialAuth,
        onSignIn,
        onSignOut,
      }}
    >
      <CurrencyProvider
        value={{
          currency: available[currencyId],
          available,
          recommended: recommended.map(code => available[code]),
          onChange,
        }}
      >
        <InitIntl raw={intl}>
          {intlFull => (
            <IntlProvider
              value={{ ...intlFull, translate: show ? R.identity : intlFull.translate }}
            >
              <ModalProvider value={{ onChange: onChangeModal }}>{children}</ModalProvider>
            </IntlProvider>
          )}
        </InitIntl>
      </CurrencyProvider>
    </AuthProvider>
  </SessionProvider>
)

const dispatcher = <T>(dispatch: Dispatch<any>, obj: { promise: Promise<T> }): Promise<T> => {
  dispatch(obj)
  return obj.promise
}

const connector: Connector<OwnProps, Props> = connect(
  state => ({
    lang: getLanguage(state),
    // intl
    show: getOptions(state).showTranslationKeys,
    // auth
    userState: state.user, // { ?user, loading, ?error }
    // currency
    currencyId: getCurrency(state),
    available: rewriteCurrencies(getCurrencies(state)),
    recommended: getRecommendedCurrenciesSelectorJS(state),
  }),
  dispatch => ({
    // auth
    onSignIn: (email, password) => dispatcher(dispatch, user.login(Map({ email, password }))),
    onSignOut: () => dispatch(user.logout()),
    onSocialAuth: provider => dispatch(user.socialAuthNitro(provider)),
    onRegister: ({ firstName, lastName, email, password }) =>
      dispatcher(dispatch, user.signup(Map({ firstName, lastName, email, password }))),
    // currency
    onChange: curr => dispatch(setCurrency(curr)),
    onChangeModal: onModalChange(dispatch),
  }),
)

export default connector(NitroBridge)
